import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import Modal from 'react-modal';
import parse from 'html-react-parser';
/* eslint-disable import/no-unresolved */
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
/* eslint-enable import/no-unresolved */
import '../../../translations/i18n';
import * as utils from '../../../utils/OrderUtils';
import styles from './Recommendations.module.scss';
import modalStyles from '../../UI/Modal/Modal.module.scss';
import { ReactComponent as MickyEarsIcon } from '../../../svgs/micky-ears.svg';
import { ReactComponent as MickyFaceIcon } from '../../../svgs/micky-face.svg';
import { ReactComponent as ExclusiveIcon } from '../../../svgs/exclusive.svg';
import { ReactComponent as WhatsappIcon } from '../../../svgs/whatsapp.svg';
import { UpsellWhatsAppClicks, UpsellTabClicks } from '../../Analytics/Upsell/Upsell';

function WhatsAppButton({
  className = 'btn btn--alt nav__info-btn nav__info-whatsapp', orderId,
}) {
  const whatsappNum = '+442036952527';
  const whatsappMessage = 'Hit send to add some magic to your Disneyland Paris stay…';
  const whatsappUrl = `https://wa.me/${whatsappNum}?text=${encodeURIComponent(whatsappMessage)}`;

  const handleWhatsAppClick = () => {
    UpsellWhatsAppClicks(orderId);
  };

  return (
    <a
      className={className}
      aria-label="Chat on WhatsApp"
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleWhatsAppClick}
    >
      <WhatsappIcon />
      WhatsApp
    </a>
  );
}

function Recommendations(props) {
  const { t } = useTranslation('common');
  const {
    upsellData, currency, orderId, tabID,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  if (orderId) {
    UpsellTabClicks(orderId);
  }

  useEffect(() => {
    Modal.setAppElement(document.body);
  }, []);

  const getMealTime = (title) => {
    if (title.includes('Breakfast')) return 'Breakfast';
    if (title.includes('Half')) return 'Breakfast + Lunch or Breakfast + Dinner';
    if (title.includes('Full')) return 'Breakfast + Lunch + Dinner';
    return null;
  };

  const getRestaurantLocations = (title) => {
    if (title.includes('Breakfast')) return ['Disney Hotels'];
    if (title.includes('Half') || title.includes('Full')) return ['Disney Parks & Disney Village', 'Disney Hotels'];
    return null;
  };

  const handleModalClick = (ticketIncludes, title) => (e) => {
    e.preventDefault();
    setModalTitle(title);
    setModalContent(ticketIncludes);
    setShowModal(!showModal);
  };

  const handleModalClose = (e) => {
    e.preventDefault();
    setModalTitle('');
    setModalContent('');
    setShowModal(!showModal);
  };

  return (
    <div id={tabID} className={styles.recommendations}>
      <h2>{t('payment.recommendation_title')}</h2>
      <div className={styles.whatsappSection}>
        <p>Our experts are ready to help you add meal plans and extras to make your holiday even more magical.</p>
        <WhatsAppButton
          className={styles.whatsappBtn}
          orderId={orderId}
        />
      </div>

      <div className={styles.carouselSection}>
        {upsellData.mealPlans && (
          <h3>Add a meal plan</h3>
        )}
        <Swiper
          spaceBetween={16}
          slidesPerView={1.2}
          slidesOffsetBefore={10}
          slidesOffsetAfter={10}
          breakpoints={{
            1023: {
              slidesPerView: 3.2,
              slidesOffsetBefore: 20,
              slidesOffsetAfter: 20,
            },
          }}
          className={styles.carouselContainer}
        >
          {upsellData.mealPlans && upsellData.mealPlans.map((mealPlan) => (
            <SwiperSlide key={mealPlan.product_title}>
              <div className={styles.carouselMealPlan}>
                <h3>{mealPlan.product_title}</h3>
                <p className={styles.fontSizeSmallest}>{getMealTime(mealPlan.product_title)}</p>
                <ul className={styles.mealPlanRestaurants}>
                  <li>
                    <MickyEarsIcon />
                    <p>
                      {getRestaurantLocations(mealPlan.product_title).map((location, index, array) => (
                        <span key={location}>
                          Restaurants in
                          {' '}
                          <span style={{ fontWeight: '600' }}>{location}</span>
                          {index < array.length - 1 && <span> + </span>}
                        </span>
                      ))}
                    </p>
                  </li>
                  <li>
                    <MickyFaceIcon />
                    <p>
                      {mealPlan.product_title.includes('Extra Plus')
                        ? 'FREE Character Dining'
                        : 'Add Character Dining for less'}
                    </p>
                  </li>
                  <li className={styles.persuasion}>
                    <ExclusiveIcon />
                    <p>
                      <strong>BONUS </strong>
                      €25 Disney Spending Money
                    </p>
                  </li>
                </ul>
                <div>
                  <a className={styles.carouselLink} href="#modal" onClick={handleModalClick(mealPlan.ticket_includes, mealPlan.product_title)}>
                    What&apos;s Included?
                  </a>
                </div>
                <div className={styles.mealPlanTotal}>
                  <h3>{utils.formatPrice(currency, mealPlan.price)}</h3>
                  <p>per stay</p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className={styles.carouselSection}>
        {upsellData.extras && (
          <h3>Magical Extras</h3>
        )}
        <Swiper
          spaceBetween={16}
          slidesPerView={1.2}
          slidesOffsetBefore={10}
          slidesOffsetAfter={10}
          breakpoints={{
            1023: {
              slidesPerView: 3.2,
              slidesOffsetBefore: 20,
              slidesOffsetAfter: 20,
            },
          }}
          className={styles.carouselContainer}
        >
          {upsellData.extras && upsellData.extras.map((extra) => (
            <SwiperSlide key={extra.product_title}>
              <div className={styles.carouselGeneral}>
                <div className={styles.carouselImg}>
                  <img alt="" src={extra.img} />
                </div>
                <div className={styles.carouselGeneralContent}>
                  <h3>{extra.product_title}</h3>
                  <p>{extra.desc_short}</p>
                  <p>
                    <a href="#modal" onClick={handleModalClick(extra.ticket_includes, extra.product_title)}>
                      What&apos;s Included?
                    </a>
                  </p>
                  <div className={styles.generalTotal}>
                    <h3>
                      Total&nbsp;
                      {utils.formatPrice(currency, extra.price)}
                    </h3>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className={styles.disclaimer}>
        <em>Pricing is dynamic and subject to change. Our Disneyland Paris Experts will confirm the final price when you reach out to add your Meal Plan or Magical Extras.</em>
      </div>

      <Modal
        isOpen={showModal}
        contentLabel={modalTitle}
        onRequestClose={handleModalClick('', '')}
        className={`${modalStyles.modal} ${styles.recommendationsModal}`}
        overlayClassName={modalStyles.modalOverlay}
      >
        <div className={styles.carouselModalContainer}>
          <h2>{modalTitle}</h2>
          <div className={modalStyles.leftText}>
            {parse(modalContent)}
          </div>
          <button className={styles.close} type="button" onClick={handleModalClose}>{t('close')}</button>
        </div>
      </Modal>
    </div>
  );
}

export default Recommendations;
